import { useEffect, useMemo, useState } from 'react';
import { Typography, useMediaQuery } from '@mui/material';
import CircularProgressLoading from '../../../../../components/Accessibility/CircularProgressLoading';
import { TranscricaoDetalhe } from '../../../../../interfaces/entities/Transcricao/TranscricaoDetalhe';
import { TranscricaoItemDetalhe } from '../../../../../interfaces/entities/Transcricao/TranscricaoRevisao';
import { recuperarTranscricaoItem } from '../../../../../services/TranscricaoBeneficiarioService';
import { useAccessibility } from '../../../../../Store/contexts/AccessibilityContext';
import { MOBILE_BREAKPOINT } from '../../transcricaoAnexoUtils';
import RevisaoSectionTable from '../../components/TranscricaoRevisao';
import {
  COLUNAS_BENEFICIARIO,
  COLUNAS_CID,
  COLUNAS_PROCEDIMENTOS,
  COLUNAS_PROFISSIONAL,
  colunasParaRowsEmpilhadas,
  mapBeneficiarioLinhas,
  mapCidLinhasDetalhe,
  mapProcedimentosParaLinhas,
  mapProfissionalLinhas,
  mapProfissionalRowsMobile,
} from '../../utils/transcricaoDadosPedidoUtils';
import TranscricaoDadosPedidoSectionCard from './TranscricaoDadosPedidoSectionCard';

interface Props {
  idTranscricao: string;
  detalhe: TranscricaoDetalhe;
}

const TranscricaoDadosPedidoTab: React.FC<Props> = ({ idTranscricao, detalhe }) => {
  const accessibility = useAccessibility();
  const isMobileLayout = useMediaQuery(MOBILE_BREAKPOINT);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [itens, setItens] = useState<TranscricaoItemDetalhe[]>([]);

  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      setErro(null);
      const resultado = await recuperarTranscricaoItem(idTranscricao);
      if (!resultado.ok) {
        setErro(resultado.message);
        setItens([]);
      } else {
        setItens(resultado.data);
      }
      setLoading(false);
    };

    void carregar();
  }, [idTranscricao]);

  const beneficiarioLinhas = useMemo(() => mapBeneficiarioLinhas(detalhe), [detalhe]);
  const profissionalLinhas = useMemo(() => mapProfissionalLinhas(detalhe), [detalhe]);
  const cidLinhas = useMemo(() => mapCidLinhasDetalhe(detalhe), [detalhe]);
  const procedimentosLinhas = useMemo(() => mapProcedimentosParaLinhas(itens), [itens]);

  const beneficiarioRows = useMemo(
    () => colunasParaRowsEmpilhadas(COLUNAS_BENEFICIARIO, beneficiarioLinhas[0]),
    [beneficiarioLinhas],
  );

  const profissionalRows = useMemo(
    () => mapProfissionalRowsMobile(profissionalLinhas),
    [profissionalLinhas],
  );

  const cidRows = useMemo(
    () => colunasParaRowsEmpilhadas(COLUNAS_CID, cidLinhas[0]),
    [cidLinhas],
  );

  const procedimentosRows = useMemo(
    () => procedimentosLinhas.map((linha) => colunasParaRowsEmpilhadas(COLUNAS_PROCEDIMENTOS, linha)),
    [procedimentosLinhas],
  );

  if (loading) {
    return <CircularProgressLoading />;
  }

  if (erro) {
    return (
      <Typography style={accessibility.textStyleAccessibility} className="transcricao-detalhe-erro">
        {erro}
      </Typography>
    );
  }

  if (isMobileLayout) {
    return (
      <div className="transcricao-dados-pedido transcricao-dados-pedido--mobile">
        <TranscricaoDadosPedidoSectionCard titulo="Beneficiário" rows={beneficiarioRows} />
        <TranscricaoDadosPedidoSectionCard titulo="Profissional" rows={profissionalRows} />
        <TranscricaoDadosPedidoSectionCard titulo="CID" rows={cidRows} />

        <section className="transcricao-dados-pedido-section">
          <Typography
            component="h3"
            style={accessibility.textStyleAccessibility}
            className="transcricao-dados-pedido-section-title"
          >
            <strong>Procedimentos</strong>
          </Typography>

          {procedimentosRows.length === 0 ? (
            <TranscricaoDadosPedidoSectionCard
              rows={colunasParaRowsEmpilhadas(COLUNAS_PROCEDIMENTOS, { id: 'empty', cells: {} })}
            />
          ) : (
            <div className="transcricao-dados-pedido-procedimentos-cards">
              {procedimentosRows.map((rows, index) => (
                <TranscricaoDadosPedidoSectionCard
                  key={procedimentosLinhas[index]?.id ?? index}
                  rows={rows}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="transcricao-dados-pedido">
      <RevisaoSectionTable
        titulo="Beneficiário"
        colunas={COLUNAS_BENEFICIARIO}
        linhas={beneficiarioLinhas}
      />
      <RevisaoSectionTable
        titulo="Profissional"
        colunas={COLUNAS_PROFISSIONAL}
        linhas={profissionalLinhas}
      />
      <RevisaoSectionTable titulo="CID" colunas={COLUNAS_CID} linhas={cidLinhas} />
      <RevisaoSectionTable
        titulo="Procedimentos"
        colunas={COLUNAS_PROCEDIMENTOS}
        linhas={procedimentosLinhas}
      />
    </div>
  );
};

export default TranscricaoDadosPedidoTab;
